import { User, UserRole } from 'shared/types';
import type { ApiResponse } from 'shared/types/api';
import { config } from 'config';
import { getStringsDirect } from 'shared/utils/stringService';
import api, { rawFetchWithAuth, ApiError, clearCache, cachedGet } from './apiClient';

// Mismo TTL corto usado en companyApi.ts para listas de tipo "dropdown" de
// baja rotación — estas no se cacheaban en absoluto, a diferencia del resto
// de servicios de la app.
const CACHE_SHORT = 30 * 1000;

export interface UserFormData {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  nacionalidad?: string;
  role: UserRole;
  company?: string;
  isActive?: boolean;
  cedula?: string;
  companies?: Array<{
    companyId: string;
    departments?: string[];
    role?: UserRole;
    position?: string;
    department?: string;
  }>;
  /** Direct reports — array of user ids. Sending this on update
   *  replaces the user's `manages` array (single-supervisor invariant
   *  enforced server-side). */
  manages?: string[];
}

export interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  /** Required on the BE side — the invitee never gets a chance to
   *  enter it during registration (identity is locked there). */
  cedula: string;
  phone?: string;
  nacionalidad?: string;
  role: UserRole;
  companyId: string;
  departments?: string[];
  requiredCompanyVerifications?: string[];
  /** Provider company only — a linked client to attach this new hire's
   *  seat request to once they accept the invite (see
   *  reserveProviderInitiatedSeat on the BE, applied at registration
   *  completion — sending the invite itself doesn't reserve the seat). */
  clientRelationshipId?: string;
}

export interface UserProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  role?: UserRole;
  isActive?: boolean;
  search?: string;
  company?: string;
  companyId?: string;
  hasCompany?: boolean;
  excludeRole?: string;
  /** Returns users whose `manages` array contains this user id —
   *  use to look up someone's supervisor(s). */
  manages?: string;
  /** Users invited via the direct-create flow who haven't activated
   *  their account yet (lastLogin unset, isActive true). Powers the
   *  Invitaciones screen. */
  pendingInvitation?: boolean;
  /** Filters to users whose membership at the scoped company (dataScope
   *  "company", or the explicit `companyId` above) is external/internal —
   *  server-side equivalent of the client-side isExternal filter
   *  SecurityUsers.tsx used to do over a single page of results. */
  isExternal?: boolean;
}

export interface UserGeneralStats {
  summary: {
    total: number;
    active: number;
    inactive: number;
    activePercentage: number;
  };
  byRole: Record<string, number>;
  byStatus: Record<string, number>;
  recent: User[];
}

export interface LoginHistoryEntry {
  id: string;
  date: string;
  success: boolean;
  ipAddress: string;
  userAgent: string;
  location?: string;
  failureReason?: string;
  device?: {
    type: string;
    os: string;
    browser: string;
  };
}

export interface LoginHistoryResponse {
  success: boolean;
  data: {
    logins: LoginHistoryEntry[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

export interface UserIndividualStats {
  profile: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    status: string;
    isActive: boolean;
    company?: string;
    lastLogin?: string;
    createdAt: string;
  };
  activity: {
    totalLogins: number;
    lastLoginDaysAgo: number;
    accountAge: number;
  };
  permissions: {
    certifications: string[];
    phone: string;
  };
}

// ApiResponse moved to shared/types/api.ts. Re-export so existing
// `import { ApiResponse } from 'shared/services/userApi'` keeps working.
export type { ApiResponse } from 'shared/types/api';

class UserApiService {
  async getSimple(params: { company: string; limit?: number; search?: string; contactScope?: 'client' }): Promise<{
    success: boolean;
    data: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      // BE's getUsersSimple omits this key entirely (not "") for callers
      // without PII-viewing privilege (client_staff/security/client_approver)
      // — declaring it required let callers assume it's always defined.
      email?: string;
      role: string;
      cedula?: string;
      isExternal?: boolean;
      externalCompanyName?: string;
    }>;
  }> {
    try {
      const queryParams: Record<string, any> = { company: params.company };
      if (params.limit) queryParams.limit = params.limit;
      if (params.search) queryParams.search = params.search;

      const result = await cachedGet('/users/simple', queryParams, CACHE_SHORT);
      return { success: true, data: result.data || [] };
    } catch {
      return { success: false, data: [] };
    }
  }

  async getAll(filters: UserFilters = {}): Promise<ApiResponse<User[]>> {
    try {
      const params: Record<string, any> = {};

      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.role) params.role = filters.role;
      if (filters.isActive !== undefined) params.isActive = filters.isActive;
      if (filters.search) params.search = filters.search;
      if (filters.company) params.company = filters.company;
      if (filters.companyId) params.companyId = filters.companyId;
      if (filters.hasCompany !== undefined) params.hasCompany = filters.hasCompany;
      if (filters.excludeRole) params.excludeRole = filters.excludeRole;
      if (filters.manages) params.manages = filters.manages;
      if (filters.pendingInvitation !== undefined) params.pendingInvitation = filters.pendingInvitation;
      if (filters.isExternal !== undefined) params.isExternal = filters.isExternal;

      const result = await api.get('/users', params);

      return {
        success: true,
        data: result.data || result.users || [],
        pagination: result.pagination
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener usuarios'
      };
    }
  }

  async getById(userId: string): Promise<ApiResponse<User>> {
    try {
      const result = await api.get(`/users/${userId}`);

      return {
        success: true,
        data: result.data || result.user || result
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener usuario'
      };
    }
  }

  async create(userData: UserFormData): Promise<ApiResponse<User>> {
    try {
      const result = await api.post('/users', userData);

      // See update()'s clearCache comment — same staleness issue applies to
      // a newly created user not showing up in the team list.
      clearCache('/companies');
      clearCache('/users');

      return {
        success: true,
        data: result.data || result.user || result,
        message: result.message || 'Usuario creado exitosamente'
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || (error instanceof Error ? error.message : 'Error al crear usuario'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al crear usuario'),
        details: errorData.details,
        field: errorData.field
      };
    }
  }

  async inviteUser(inviteData: InviteUserData): Promise<ApiResponse<{
    invitationCode: string;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    company: { id: string; name: string };
    expiresAt: string;
    emailSent: boolean;
    emailError: string | null;
  }>> {
    try {
      const result = await api.post('/users/invite', inviteData);

      return {
        success: true,
        data: result.data || result,
        message: result.message || 'Invitación enviada exitosamente'
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || errorData.error || (error instanceof Error ? error.message : 'Error al invitar usuario'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al invitar usuario'),
        details: errorData.details,
        field: errorData.field
      };
    }
  }

  /**
   * `companyContext` MUST be passed whenever the target user could belong
   * to more than one active company (which the caller usually can't know
   * in advance) and the edit is happening from a specific company's page —
   * e.g. CompanyUserForm.tsx passes its URL's :companyId. Without it, the
   * BE's requireCompanyContextForModification middleware only auto-infers
   * a company when the caller/target has EXACTLY one active membership;
   * multi-company targets now correctly 400 instead of the previous
   * behavior of silently guessing the wrong one (a real incident: editing
   * a user's role from company A's page landed the change on that same
   * user's unrelated company B membership instead).
   */
  async update(userId: string, userData: Partial<UserFormData>, companyContext?: string): Promise<ApiResponse<User>> {
    try {
      const result = await api.put(`/users/${userId}`, userData, companyContext ? { params: { companyContext } } : undefined);

      // The company-scoped team list (Users.tsx, /my-company/team and
      // /companies/:id/users) reads users via companyApi.getById(companyId),
      // which is cachedGet'd for 30s — without this, an edit shows stale
      // data on the list until the cache naturally expires or a hard reload.
      // Broad pattern match (mirrors companyApi.createRelationship) since
      // this update doesn't know which company's cache entry to target.
      clearCache('/companies');
      clearCache('/users');

      return {
        success: true,
        data: result.data || result.user || result,
        message: result.message || 'Usuario actualizado exitosamente'
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || (error instanceof Error ? error.message : 'Error al actualizar usuario'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al actualizar usuario'),
        details: errorData.details,
        field: errorData.field
      };
    }
  }

  /**
   * Toggle access for a user in a specific company (Desactivar / Reactivar).
   * Does NOT free the seat — the user still counts toward maxUsers.
   * Allowed for: company_admin of that company, super_admin.
   *
   * Reactivation cooldown: a user removed less than 6 months ago can only be
   * reactivated by super_admin with `override: true` and a `reason`. Auto-bypassed
   * by the server if the user has an in-progress verification submitted before
   * removal.
   */
  async setCompanyAccess(
    userId: string,
    companyId: string,
    enabled: boolean,
    opts?: { reason?: string; override?: boolean },
  ): Promise<ApiResponse<{ cooldown?: boolean; cooldownEndsAt?: string; requiresOverride?: boolean }>> {
    try {
      await api.patch(`/users/${userId}/companies/${companyId}/access`, {
        enabled,
        ...(opts?.reason ? { reason: opts.reason } : {}),
        ...(opts?.override ? { override: true } : {}),
      });
      // See update()'s clearCache comment — per-company active state is shown in the list.
      clearCache('/companies');
      clearCache('/users');
      return { success: true, message: enabled ? 'Usuario reactivado' : 'Usuario desactivado' };
    } catch (error: any) {
      // apiClient throws a plain ApiError ({success, error, statusCode,
      // details}), not a real Error — `.message` doesn't exist on it, and
      // the structured fields (cooldown/requiresOverride/cooldownEndsAt)
      // are the raw JSON body under `.details`, not spread onto the
      // top-level object.
      return {
        success: false,
        message: error?.error || error?.message || 'No se pudo cambiar el acceso',
        data: {
          cooldown: !!error?.details?.cooldown,
          cooldownEndsAt: error?.details?.cooldownEndsAt,
          requiresOverride: !!error?.details?.requiresOverride,
        },
      } as any;
    }
  }

  /**
   * Set the full set of platform roles a platform user holds
   * (super_admin and/or safety_staff). Super_admin only. Returns the updated
   * active role + set.
   */
  async setGlobalRoles(
    userId: string,
    globalRoles: string[],
  ): Promise<ApiResponse<{ _id: string; globalRole: string; globalRoles: string[] }>> {
    try {
      const res = await api.patch(`/users/${userId}/global-roles`, { globalRoles });
      return { success: true, data: (res as any)?.data ?? (res as any) };
    } catch (error: any) {
      return { success: false, message: error?.message || 'No se pudieron actualizar los roles' } as any;
    }
  }

  /**
   * Remove a user from a specific company (Quitar de empresa) — frees the seat.
   * Super_admin only. Within first 6 months of joining, a `reason` is required
   * (audit-logged override). If `replacementUserId` is given, also reassigns
   * that user in every active document at this company that still references
   * the removed user (companyContact/contractorContact/workers/pending
   * approvalChain) — same mechanism as approving a removal request with a
   * chosen replacement.
   */
  async removeFromCompany(
    userId: string,
    companyId: string,
    reason?: string,
    replacementUserId?: string,
  ): Promise<ApiResponse<{ within6Months?: boolean }>> {
    try {
      await api.post(`/users/${userId}/companies/${companyId}/remove`, {
        ...(reason ? { reason } : {}),
        ...(replacementUserId ? { replacementUserId } : {}),
      });
      // See update()'s clearCache comment — the user must disappear from the list.
      clearCache('/companies');
      clearCache('/users');
      return { success: true, message: 'Usuario quitado de la empresa' };
    } catch (error: any) {
      return {
        success: false,
        message: error?.error || error?.message || 'No se pudo quitar al usuario',
        data: { within6Months: !!error?.details?.within6Months },
      } as any;
    }
  }

  async getGeneralStats(): Promise<ApiResponse<UserGeneralStats>> {
    try {
      const result = await api.get('/users/stats');

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener estadísticas generales'
      };
    }
  }

  async getStats(userId: string): Promise<ApiResponse<UserIndividualStats>> {
    try {
      const result = await api.get(`/users/${userId}/stats`);

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener estadísticas del usuario'
      };
    }
  }

  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const result = await api.get('/users/profile');

      return {
        success: true,
        data: result.data || result.user || result
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener perfil'
      };
    }
  }

  async updateProfile(profileData: UserProfileData): Promise<ApiResponse<User>> {
    try {
      const result = await api.put('/users/profile', profileData);

      return {
        success: true,
        data: result.data || result.user || result,
        message: result.message || 'Perfil actualizado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar perfil'
      };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.put('/users/change-password', { currentPassword, newPassword });

      return {
        success: true,
        message: result.message || 'Contraseña cambiada exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al cambiar contraseña'
      };
    }
  }

  async toggleStatus(userId: string): Promise<ApiResponse<User>> {
    try {
      const result = await api.put(`/users/${userId}/toggle-status`);

      // See update()'s clearCache comment — isActive is shown in the list.
      clearCache('/companies');
      clearCache('/users');

      return {
        success: true,
        data: result.data || result.user || result,
        message: result.message || 'Estado del usuario actualizado'
      };
    } catch (error: unknown) {
      const apiErrorMessage = (error as { error?: string } | null)?.error;
      return {
        success: false,
        message:
          apiErrorMessage ||
          (error instanceof Error ? error.message : 'Error al cambiar estado del usuario')
      };
    }
  }

  /**
   * Resend the welcome / set-password email to a user who hasn't logged in
   * yet. BE rejects (400 ALREADY_ACTIVATED) when the target already has a
   * `lastLogin`; surface that to the caller via `success: false`.
   */
  async resendInvitation(userId: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.post(`/users/${userId}/resend-invitation`);
      return {
        success: true,
        message: result.message || 'Invitación reenviada exitosamente'
      };
    } catch (error: unknown) {
      const apiErrorMessage = (error as { error?: string } | null)?.error;
      return {
        success: false,
        message:
          apiErrorMessage ||
          (error instanceof Error ? error.message : 'Error al reenviar la invitación')
      };
    }
  }

  /**
   * Backup path for when the invitation email itself isn't reaching the
   * user: mints a new temp password and returns it once. Never emailed —
   * the caller is responsible for relaying it to the user out-of-band.
   * Same gate as resendInvitation (400 ALREADY_ACTIVATED / INACTIVE).
   */
  async generatePassword(userId: string): Promise<ApiResponse<{ tempPassword: string; email: string }>> {
    try {
      const result = await api.post(`/users/${userId}/generate-password`);
      return {
        success: true,
        data: result.data,
        message: result.message || 'Contraseña temporal generada',
      };
    } catch (error: unknown) {
      const apiErrorMessage = (error as { error?: string } | null)?.error;
      return {
        success: false,
        message:
          apiErrorMessage ||
          (error instanceof Error ? error.message : 'Error al generar la contraseña'),
      };
    }
  }

  /**
   * Revoke a pending invitation: clears the set-password token and
   * deactivates the row. BE refuses when the user has already logged
   * in — UI hides the action in that case too.
   */
  async revokeInvitation(userId: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.post(`/users/${userId}/revoke-invitation`);
      // See update()'s clearCache comment — status shown in the list changes.
      clearCache('/companies');
      clearCache('/users');
      return {
        success: true,
        message: result.message || 'Invitación revocada',
      };
    } catch (error: unknown) {
      const apiErrorMessage = (error as { error?: string } | null)?.error;
      return {
        success: false,
        message:
          apiErrorMessage ||
          (error instanceof Error ? error.message : 'Error al revocar la invitación'),
      };
    }
  }

  // super_super_admin only — BE 403s otherwise.
  async hardDeleteUser(userId: string): Promise<ApiResponse<void>> {
    try {
      const result = await api.delete(`/users/${userId}`);
      // See update()'s clearCache comment — the user must disappear from the list.
      clearCache('/companies');
      clearCache('/users');
      return {
        success: true,
        message: result.message || 'Usuario eliminado',
      };
    } catch (error: unknown) {
      const apiErrorMessage = (error as { error?: string } | null)?.error;
      return {
        success: false,
        message:
          apiErrorMessage ||
          (error instanceof Error ? error.message : 'Error al eliminar el usuario'),
      };
    }
  }

  async getLoginHistory(
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      success?: boolean;
    }
  ): Promise<LoginHistoryResponse> {
    try {
      const queryParams: Record<string, any> = {};

      if (params?.page) queryParams.page = params.page;
      if (params?.limit) queryParams.limit = params.limit;
      if (params?.success !== undefined) queryParams.success = params.success;

      const result = await api.get(`/users/${userId}/login-history`, queryParams);

      return {
        success: true,
        data: {
          logins: result.data.logins || [],
          pagination: result.data.pagination || { current: 1, pages: 1, total: 0, limit: 10 }
        }
      };
    } catch (error: unknown) {
      return {
        success: false,
        data: {
          logins: [],
          pagination: { current: 1, pages: 1, total: 0, limit: 10 }
        }
      };
    }
  }

  async updateSupervisedCompanies(userId: string, companyIds: string[]): Promise<ApiResponse<User>> {
    try {
      const result = await api.put(`/users/${userId}/supervised-companies`, { companyIds });

      const strings = getStringsDirect();

      return {
        success: true,
        data: result.data || result.user || result,
        message: result.message || `${strings.sedes} supervisadas actualizadas exitosamente`
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar empresas supervisadas'
      };
    }
  }

  async getSupervisedCompanies(userId: string): Promise<ApiResponse<{ _id: string; name: string }[]>> {
    try {
      const result = await api.get(`/users/${userId}/supervised-companies`);

      return {
        success: true,
        data: result.data || result.companies || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener empresas supervisadas'
      };
    }
  }

  async exportUsers(filters: UserFilters & { format?: 'csv' | 'excel' }): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.role) params.append('role', filters.role);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.company) params.append('company', filters.company);
    if (filters.companyId) params.append('companyId', filters.companyId);
    params.append('format', filters.format || 'excel');

    const response = await rawFetchWithAuth(
      `${config.apiUrl}/users/export?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error('Error al exportar los usuarios');
    }

    return response.blob();
  }

  async getBadgeProfile(userId: string): Promise<ApiResponse<any>> {
    try {
      const result = await api.get(`/users/${userId}/badge`);

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || (error instanceof Error ? error.message : 'Error al obtener carnet'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al obtener carnet')
      };
    }
  }

  async getBadgeAvatar(userId: string): Promise<ApiResponse<any>> {
    try {
      const result = await api.get(`/users/${userId}/badge`, { avatar: 'true' });

      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || (error instanceof Error ? error.message : 'Error al obtener avatar'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al obtener avatar')
      };
    }
  }

  async uploadProfilePicture(userId: string, file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await rawFetchWithAuth(`${config.apiUrl}/users/${userId}`, {
        method: 'PUT',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          message: result.message || 'Error al subir foto',
          error: result.error || result.message
        };
      }

      return {
        success: true,
        data: result.data,
        message: result.message || 'Foto subida exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al subir foto',
        error: error instanceof Error ? error.message : 'Error al subir foto'
      };
    }
  }

  async getProfilePictureUploadUrl(contentType: string): Promise<ApiResponse<any>> {
    try {
      const result = await api.post('/users/profile/picture/upload-url', { contentType });

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener URL de carga'
      };
    }
  }

  async confirmProfilePictureUpload(key: string): Promise<ApiResponse<any>> {
    try {
      const result = await api.post('/users/profile/picture/confirm', { key });

      return {
        success: true,
        data: result.data,
        message: result.message || 'Foto de perfil actualizada'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al confirmar carga de foto'
      };
    }
  }

  async getProfilePictureDownloadUrl(): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
    try {
      const result = await api.get('/users/profile/picture/download');

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      // Handle the case where user has no profile picture
      if ((error as ApiError)?.statusCode === 204) {
        return { success: false, message: 'No tiene foto de perfil' };
      }
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener URL de descarga'
      };
    }
  }

  /**
   * Get user's aggregated work permit rating
   * Returns the average rating calculated from all completed work permits
   * where the user was assigned as Principal or Support worker
   */
  async getWorkPermitRating(userId: string, recalculate?: boolean): Promise<ApiResponse<UserWorkPermitRating>> {
    try {
      const params: Record<string, any> = {};
      if (recalculate) params.recalculate = true;

      const result = await api.get(`/users/${userId}/work-permit-rating`, params);

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || errorData.error || (error instanceof Error ? error.message : 'Error al obtener calificación'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al obtener calificación')
      };
    }
  }

  async getAvailableProveedores(companyId: string): Promise<ApiResponse<ProveedorLookupResult[]>> {
    try {
      const result = await cachedGet('/users/available-proveedores', { companyId }, CACHE_SHORT);

      return {
        success: true,
        data: result.data || []
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || errorData.error || (error instanceof Error ? error.message : 'Error al obtener proveedores disponibles'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al obtener proveedores disponibles')
      };
    }
  }

  async lookupProveedor(cedula: string, companyId: string): Promise<ApiResponse<ProveedorLookupResult>> {
    try {
      const result = await cachedGet('/users/lookup-proveedor', { cedula, companyId }, CACHE_SHORT);

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      const errorData = (error as ApiError)?.details || {};
      return {
        success: false,
        message: errorData.message || errorData.error || (error instanceof Error ? error.message : 'Error al buscar usuario proveedor'),
        error: errorData.error || errorData.message || (error instanceof Error ? error.message : 'Error al buscar usuario proveedor')
      };
    }
  }
}

export interface ProveedorLookupResult {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  cedula: string;
  phone?: string;
  nacionalidad?: string;
  proveedorCompany: {
    _id: string;
    name: string;
  };
}

// User Work Permit Rating type
export interface UserWorkPermitRating {
  overall: number;
  totalRatings: number;
  calidad: number;
  seguridad: number;
  puntualidad: number;
  comunicacion: number;
  responsabilidad: number;
  lastRatedAt: string | null;
  recalculated?: boolean;
}

export const userApi = new UserApiService();

/**
 * Legacy UserService compatibility layer.
 * Delegates to userApi methods with response format matching the old UserService interface.
 */
export class UserService {
  static async getUsers(filters?: UserFilters): Promise<{
    users: User[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const result = await userApi.getAll(filters || {});
    if (result.success && result.data) {
      return {
        users: result.data,
        total: result.pagination?.total || result.data.length,
        page: result.pagination?.page || 1,
        // The BE's getUsers sends pagination.pages, not .totalPages — this
        // read the wrong field and always reported exactly 1 page.
        totalPages: result.pagination?.pages ?? result.pagination?.totalPages ?? 1,
      };
    }
    return { users: [], total: 0, page: 1, totalPages: 1 };
  }

  /** `contactScope: 'client'` pide los admins/aprobadores del cliente — la
   *  única gente del cliente que un proveedor puede elegir como contraparte
   *  ("Contacto de Empresa" de una Ficha). Sin ese parámetro, un usuario
   *  prestado por un proveedor solo ve a los suyos. */
  static async getUsersByCompany(companyId: string, search?: string, contactScope?: 'client'): Promise<Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    cedula?: string;
    isExternal?: boolean;
    externalCompanyName?: string;
  }>> {
    try {
      const result = await userApi.getSimple({ company: companyId, limit: 100, search, ...(contactScope ? { contactScope } : {}) });
      return (result.data || []).map((user: any) => ({
        id: user._id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        role: user.role,
        cedula: user.cedula,
        isExternal: user.isExternal,
        externalCompanyName: user.externalCompanyName,
      }));
    } catch {
      return [];
    }
  }
}
